import React from 'react'
import type { KambiValidReward, KambiRewardType } from '@mobi/api-types/kambi/player/reward'
import type { KambiSportsSelection } from '@mobi/betslip/types'
import { isKambiSportsSelection } from '@mobi/betslip/helpers/typeGuards'
import { PromoBanner } from '@mobi/betslip/Components/Common/PromoBanner'
import { PromoSelector } from '@mobi/betslip/Components/Common/PromoSelector'
import { PromoSelectionSimple } from '@mobi/betslip/Components/Common/PromoSelector/Components/PromoSelectionSimple'
import type { PromoSelectionObject } from '@mobi/betslip/Components/Common/PromoSelector/types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { colors, radius, spacing } from '@mobi/component-library/Foundations'
import { toTitleCase } from '@mobi/utils/string'
import { toMoney } from '@mobi/utils/money'
import { observeImmutable } from '@core/Components/HOCs'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { SelectKambiReward } from '@core/Areas/Quickbet/signals'
import { formatKambiCurrencyToDollarDecimal } from '@core/Areas/KambiSports/helpers/currencyFormat'
import { useGetKambiRewardsForSelection } from './hooks/useGetKambiRewardsForSelection'

export const KambiRewardContainer: React.ComponentClass = observeImmutable(
  quickbetState$,
  ({ record }) => {
    const quickbetState = record.toJS() as QuickbetState
    if (!isKambiSportsSelection(quickbetState.selection)) return null

    return (
      <KambiRewardContainerComponent
        canConfirmBet={quickbetState.canConfirmBet}
        selection={quickbetState.selection}
        selectedReward={quickbetState.selectedKambiReward}
      />
    )
  }
)

// ===============
// Local Component
// ===============

const KambiRewardContainerComponent: React.FC<{
  canConfirmBet: boolean
  selection: KambiSportsSelection
  selectedReward: KambiValidReward | null
}> = ({ canConfirmBet, selection, selectedReward }) => {
  const rewardInfo = useGetKambiRewardsForSelection(selection)

  if (!rewardInfo || (!selectedReward && canConfirmBet)) return null

  if (canConfirmBet) {
    if (!selectedReward) return null

    return (
      <div style={{ marginBottom: spacing.md }}>
        <PromoBanner
          promoType='KambiReward'
          text={toTitleCase(selectedReward.rewardType.replaceAll('_', ' '))}
        />
      </div>
    )
  }

  const allValidRewards = rewardInfo
    ? [
        ...(rewardInfo.validRewards ?? []),
        ...(rewardInfo.validGroupRewards ?? []),
        //should not be displayed to the punter
        // ...(rewardInfo.implicitGroupRewards ?? []),
      ]
    : []

  if (allValidRewards.length === 0) return null

  const isSingleRewardActive = allValidRewards.length === 1

  const promotionObj = rewardToPromoSelection(
    allValidRewards.map(validReward => validReward.reward),
    selectedReward
  )

  return (
    <div style={{ marginBottom: spacing.md }}>
      {isSingleRewardActive ? (
        <PromoSelectionSimple
          promoType='KambiReward'
          title={promotionObj[0].title}
          isSelected={promotionObj[0].isSelected}
          hasActiveSelection
          onClick={promotionObj[0].onClick as () => void}
          descriptionSlots={promotionObj[0].descriptionSlots}
          setSelectionHeight={() => null}
          isPromoSelectorExpanded
          renderPosition={1}
          isSingularDisplay
          style={{
            background: colors.lavender[50],
            border: `1px solid ${colors.lavender[300]}`,
            borderRadius: radius.lgx1,
            padding: `${spacing.sm} ${spacing.md}`,
          }}
        />
      ) : (
        <PromoSelector
          key='promo-selector-kambi-reward'
          promoType='KambiReward'
          itemId='promo-selector-kambi-reward'
          selections={promotionObj}
        />
      )}
    </div>
  )
}

// =============
// Local Helpers
// =============

const RewardDetailMap: Record<KambiRewardType, { heading: string; description: string }> = {
  FREE_BET: {
    heading: 'Free Bet',
    description:
      'A free bet can be used instead of real money to fund a bet. If the bet wins then the payout will not include the stake of the bet.',
  },
  PROFIT_BOOST: {
    heading: 'Profit Boost',
    description: 'A profit boost is a reward that increases the combination odds.',
  },
  ODDS_BOOST: {
    heading: 'Odds Boost',
    description: 'An odds boost is a reward that specifies fixed odds for an outcome.',
  },
  BEST_ODDS: {
    heading: 'Best Odds',
    description: 'Best odds guaranteed reward is specific to horse and dog racing. ',
  },
  SECOND_CHANCE: {
    heading: 'Second Chance',
    description:
      'A second chance bet is a reward that gives you a free bet if your first bet loses.',
  },
}

function rewardToPromoSelection(
  validRewards: KambiValidReward[],
  selectedValidReward: KambiValidReward | null
): PromoSelectionObject[] {
  return validRewards.map(reward => {
    const rewardDetail = RewardDetailMap[reward.rewardType]
    const stake = getStake(reward)

    return {
      title: rewardDetail.heading,
      onClick: () => SelectKambiReward(reward),
      isSelected: selectedValidReward?.rewardId === reward.rewardId,
      selectionId: reward.rewardId,
      descriptionSlots: [
        <div key='reward-description'>{rewardDetail.description}</div>,
        stake && (
          <Flex key='reward-stake' alignItems='center' gap={spacing.smx2}>
            <Icon name='LineInfoCircle' color={colors.neutral[800]} size='1.2rem' />
            Max stake : {toMoney(formatKambiCurrencyToDollarDecimal(stake))}
          </Flex>
        ),
      ],
    }
  })
}

function getStake(reward: KambiValidReward): number | null | undefined {
  switch (reward.rewardType) {
    case 'FREE_BET':
      return reward.freeBetProperties?.amount ?? null
    case 'PROFIT_BOOST':
      return reward.profitBoostProperties?.maxStake ?? null
    case 'ODDS_BOOST':
      return reward.oddsBoostProperties?.maxStake ?? null
    case 'SECOND_CHANCE':
      return reward.secondChanceProperties?.maxStake ?? null
    default:
      return null
  }
}
